import { Option } from 'api/types/_public';
import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from 'common/toast';
import BreadCrumb from 'components/Common/BreadCrumb';
import TooltipCustom from 'components/Common/TooltipCustom';
import { useRole } from 'components/Hooks/UserHooks';
import SVGImageDefault from 'components/Images/SVGImageDefault';
import { SYSTEM_CONFIG_KEY } from 'helpers/constans';
import { formatLinkHTTPS } from 'helpers/format';
import { isHavePermissionRole, ROLES_FOR_APP } from 'helpers/role';
import React, { useEffect, useState } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable
} from 'react-beautiful-dnd';
import { useTranslation } from "react-i18next";
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Card, CardBody, Col, Container, Modal, ModalBody, ModalHeader, Row, Spinner } from "reactstrap";
import { getAllMediaAdsHub, getConfiguration, putConfiguration } from 'store/thunks';


interface Item {
  id: number;
  code: number,
  name: string;
  logo_url: string;
  website: string
}

const menuStyle: React.CSSProperties = {
  width: '30px',
  height: '4px',
  backgroundColor: '#878a99',
  margin: '6px 0',
  borderRadius: '3px'
};

const reorder = (list: Item[], startIndex: number, endIndex: number): Item[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const move = (
  source: Item[],
  destination: Item[],
  droppableSource: any,
  droppableDestination: any
): { [key: string]: Item[] } => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);

  destClone.splice(droppableDestination.index, 0, removed);

  const result: { [key: string]: Item[] } = {};
  result[droppableSource.droppableId] = sourceClone;
  result[droppableDestination.droppableId] = destClone;

  return result;
};

const grid = 8;

const getItemStyle = (isDragging: boolean, draggableStyle: React.CSSProperties): React.CSSProperties => ({
  userSelect: 'none',
  padding: grid * 2,
  margin: `0 0 ${grid}px 0`,
  background: isDragging ? '#eaeaea' : '#fff',
  borderRadius: '4px',
  ...draggableStyle
});

const getListStyle = (isDraggingOver: boolean): React.CSSProperties => ({
  background: isDraggingOver ? '#F0F9FF' : '#F7F9FC',
  padding: '0px 8px 8px 8px',
  width: 265 + (window.innerWidth > 1600 ? 70 : 0),
  borderRadius: '8px',
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
  margin: '0 20px',
  height: '70dvh',
  minHeight: '400px',
  overflowY: 'auto'
});

const Configuration = () => {
  const { t, i18n } = useTranslation();
  const { userPermissions } = useRole();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [medias, setMedias] = useState<Option[]>([]);

  const [items, setItems] = useState<Item[]>([]);
  const [selected, setSelected] = useState<Item[]>([]);
  const [filterItems, setFilterItems] = useState('');
  const [filterSelected, setFilterSelected] = useState('');

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleSubmit = async () => {
    if (isLoading) {
      return;
    }
    setIsOpen((_prev) => true);
  };

  const handleSubmitConfiguration = async () => {
    try {
      if (isLoading) {
        return;
      }

      setIsLoading((_prev) => true);
      const ads_hub_outlet_media_ids = (selected || [])?.map((_m: any) => _m?.id)?.filter((_m: any) => !!_m);
      const _data_configs = {
        configs: [
          {
            key: SYSTEM_CONFIG_KEY.KEYWORD_REAL_TIME_ADS_HUB_OUTLET_MEDIA_IDS,
            value: JSON.stringify(ads_hub_outlet_media_ids),
            is_active: 1,
          },
        ]
      };

      const [res1]: any = await Promise.all([putConfiguration(_data_configs)]);
      setIsOpen((_prev) => false);
      setIsLoading((_prev) => false);
      if (res1?.code === 200) {
        toast(`${t('The process has been completed.')}`, CONFIG_OPTION_TOAST_NORMAL);
      } else {
        toast(`${res1?.message || res1}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsOpen((_prev) => false);
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const getList = (id: string) => {
    if (id === 'droppable') {
      return items;
    }
    return selected;
  };

  const onDragEnd = (result: any) => {
    const { source, destination } = result;

    if (!destination) {
      return;
    }

    const sourceList = getList(source.droppableId);
    const destinationList = getList(destination.droppableId);

    // Get the actual source and destination indexes based on the filtered list
    const sourceIndex = sourceList.findIndex(item => item.id === parseInt(result.draggableId, 10));
    const destinationIndex = destination.index;

    if (source.droppableId === destination.droppableId) {
      const reorderedItems = reorder(
        sourceList,
        sourceIndex,
        destinationIndex
      );

      if (source.droppableId === 'droppable') {
        setItems(reorderedItems);
      } else {
        setSelected(reorderedItems);
      }
    } else {
      const movedResult = move(
        sourceList,
        destinationList,
        { index: sourceIndex, droppableId: source.droppableId },
        { index: destinationIndex, droppableId: destination.droppableId }
      );

      setItems(movedResult.droppable || items);
      setSelected(movedResult.droppable2 || selected);
      // handleSelect && handleSelect({
      //   inventory_active_ids: movedResult.droppable.map((item: any) => ({ value: String(item.id), label: item.name || '' })) || [],
      //   inventory_inactive_ids: movedResult.droppable2.map((item: any) => ({ value: String(item.id), label: item.name || '' })) || [],
      // });
    }
  };

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(filterItems.toLowerCase()) || item?.name.toLowerCase().includes(filterItems.toLowerCase()) || item?.website.toLowerCase().includes(filterItems.toLowerCase())
  );

  const filteredSelected = selected.filter(item =>
    item.name.toLowerCase().includes(filterSelected.toLowerCase()) || item?.name.toLowerCase().includes(filterSelected.toLowerCase()) || item?.website.toLowerCase().includes(filterSelected.toLowerCase())
  );

  const handleMoveItems = (type: 'LEFT' | 'RIGHT') => {
    if (type === 'LEFT') {
      setItems((_prev: any) => ([..._prev, ...selected]));
      setSelected((_prev: any) => ([]));
    }
    if (type === 'RIGHT') {
      setSelected((_prev: any) => ([..._prev, ...items]));
      setItems((_prev: any) => ([]));
    }
  };

  const handleCallAllOption = async () => {
    try {
      const [res1, res2]: any = await Promise.all([getAllMediaAdsHub({}), getConfiguration({})]);
      const _medias = (res1?.data || [])?.map((_c: any) => ({ value: `${_c?.id}`, label: `${_c?.name} ${_c?.website ? `(${_c?.website})` : ''}` }));
      setMedias((_prev) => (_medias));
      if (res2?.data && res2?.data) {

        const _configs = res2?.data?.list || [];

        const media_ids = _configs?.find((item: any) => item?.key === SYSTEM_CONFIG_KEY.KEYWORD_REAL_TIME_ADS_HUB_OUTLET_MEDIA_IDS)?.value || [];

        const activated = (res1?.data || [])?.filter((_m: any) => media_ids?.includes(_m?.id));

        const inactivated = (res1?.data || [])?.filter((_m: any) => !media_ids?.includes(_m?.id));

        setItems(() => inactivated);
        setSelected(() => activated);
      }
    } catch (error: any) {
      return error;
    }
  };

  useEffect(() => {
    handleCallAllOption();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = `${t('Configuration')} - ${t('Real Time Keyword')} | PRESS Ai`;
    document.body.classList.remove('vertical-sidebar-enable');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t('Real Time Keyword')} pageTitle={t('Configuration')} />
          <Row>
            {isHavePermissionRole(ROLES_FOR_APP.CONFIGURATION_BOOSTING_NEWS, userPermissions) && (
              <Col lg={12}>
                <Card id="customerList" style={{ boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)', minHeight: '75dvh' }}>
                  <CardBody className="pt-3 mb-3">
                    <div style={{ maxWidth: '840px', margin: '0 auto' }}>
                      <Row>
                        <Col md={12} className="px-0">
                          <DragDropContext onDragEnd={onDragEnd}>
                            <div className="d-flex flex-wrap flex-sm-nowrap justify-content-end gap-0 gap-md-1">
                              <div className="mb-3 mb-lg-0 w-col-auto">
                                <h5 style={{ marginLeft: '20px', marginBottom: '20px' }} >{t('Inactivated')} ({t('Adshub media outlets')})
                                </h5>
                                <Droppable droppableId="droppable">
                                  {(provided: any, snapshot: any) => (
                                    <div
                                      ref={provided.innerRef}
                                      style={getListStyle(snapshot.isDraggingOver)}>
                                      <input
                                        type="text"
                                        placeholder={`${t('Search')}...`}
                                        value={filterItems}
                                        onChange={(e) => setFilterItems(e.target.value)}
                                        style={{
                                          marginBottom: '10px',
                                          padding: '8px',
                                          width: '100%',
                                          boxSizing: 'border-box',
                                          borderRadius: '4px',
                                          border: '1px solid #ccc',
                                          position: 'sticky',
                                          top: '0px',
                                          zIndex: '999',
                                          marginTop: '16px'
                                        }}
                                      />
                                      {filteredItems.length > 0 ? filteredItems.map((item, index) => (
                                        <Draggable
                                          key={item.id}
                                          draggableId={item.id.toString()}
                                          index={index}>
                                          {(provided: any, snapshot: any) => (
                                            <div>
                                              <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                style={getItemStyle(
                                                  snapshot.isDragging,
                                                  provided.draggableProps.style
                                                )}
                                                className="d-flex align-items-center"
                                              >
                                                <div className="d-flex align-items-center justify-content-between w-100">
                                                  <div className="d-flex align-items-center">
                                                    <div className="me-2">
                                                      <Link className='text-normal flex-shrink-0 ' to={formatLinkHTTPS(item?.website)} target="_blank">
                                                        {item?.logo_url ? <img src={item?.logo_url} loading="lazy" alt={item?.name} className="avatar-sm object-fit-contain" style={{ backgroundColor: 'rgb(234 234 234 / 49%)', borderRadius: '5px' }} /> : <SVGImageDefault style={{ height: '48px' }} />}
                                                      </Link>
                                                    </div>
                                                    <div>
                                                      <div className="fs-14" style={{ lineHeight: '14px' }}>
                                                        {item?.name || ''}
                                                      </div>
                                                      <Link className='fs-12 text-normal flex-shrink-0' to={formatLinkHTTPS(item?.website)} target="_blank">
                                                        {item?.website || ''}
                                                      </Link>
                                                    </div>
                                                  </div>
                                                  <div className="ms-2">
                                                    <div style={menuStyle} />
                                                    <div style={menuStyle} />
                                                    <div style={menuStyle} />
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                        </Draggable>
                                      )) : <div className="text-center mt-3">
                                        {isLoading ? (
                                          <Spinner size="sm"></Spinner>
                                        ) :
                                          <>{t('No items found')}</>
                                        }
                                      </div>}
                                      {provided.placeholder}
                                    </div>
                                  )}
                                </Droppable>
                              </div>
                              <div className="w-col-auto" style={{ width: '34px' }}>
                                <div className="d-flex flex-column justify-content-center h-100 gap-2">
                                  <button className="btn btn-sm btn-light" onClick={() => handleMoveItems('LEFT')}>
                                    <i className="bx bx-chevrons-left pt-1 fs-16" />
                                  </button>
                                  <button className="btn btn-sm btn-light" onClick={() => handleMoveItems('RIGHT')}>
                                    <i className="bx bx-chevrons-right pt-1 fs-16" />
                                  </button>
                                </div>
                              </div>
                              <div className="w-col-auto">
                                <h5 style={{ marginLeft: '20px', marginBottom: '20px' }}>{t('Activated')} ({t('Adshub media outlets')})
                                  <TooltipCustom
                                    title={t("Select the Adshub media outlets from which to collect real-time keywords.")}
                                    className="d-inline-block vertical-align-middle ms-1"
                                    style={{ transform: 'translateY(1px)' }}
                                  >
                                    <i className="ri-question-line align-bottom text-secondary" ></i>
                                  </TooltipCustom>
                                </h5>
                                <Droppable droppableId="droppable2" >
                                  {(provided: any, snapshot: any) => (
                                    <div
                                      ref={provided.innerRef}
                                      style={getListStyle(snapshot.isDraggingOver)}>
                                      <input
                                        type="text"
                                        placeholder={`${t('Search')}...`}
                                        value={filterSelected}
                                        onChange={(e) => setFilterSelected(e.target.value)}
                                        style={{
                                          marginBottom: '10px',
                                          padding: '8px',
                                          width: '100%',
                                          boxSizing: 'border-box',
                                          borderRadius: '4px',
                                          border: '1px solid #ccc',
                                          position: 'sticky',
                                          top: '0px',
                                          zIndex: '999',
                                          marginTop: '16px'
                                        }}
                                      />
                                      {filteredSelected.length > 0 ? filteredSelected.map((item, index) => (
                                        <Draggable
                                          key={item.id}
                                          draggableId={item.id.toString()}
                                          index={index}>
                                          {(provided: any, snapshot: any) => (
                                            <div>
                                              <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                style={getItemStyle(
                                                  snapshot.isDragging,
                                                  provided.draggableProps.style
                                                )}
                                                className="d-flex align-items-center"
                                              >
                                                <div className="d-flex align-items-center justify-content-between w-100">
                                                  <div className="d-flex align-items-center">
                                                    <div className="me-2">
                                                      <Link className='text-normal flex-shrink-0 ' to={formatLinkHTTPS(item?.website)} target="_blank">
                                                        {item?.logo_url ? <img src={item?.logo_url} loading="lazy" alt={item?.name} className="avatar-sm object-fit-contain" style={{ backgroundColor: 'rgb(234 234 234 / 49%)', borderRadius: '5px' }} /> : <SVGImageDefault style={{ height: '48px' }} />}
                                                      </Link>
                                                    </div>
                                                    <div>
                                                      <div className="fs-14" style={{ lineHeight: '14px' }}>
                                                        {item?.name || ''}
                                                      </div>
                                                      <Link className='fs-12 text-normal flex-shrink-0' to={formatLinkHTTPS(item?.website)} target="_blank">
                                                        {item?.website || ''}
                                                      </Link>
                                                    </div>
                                                  </div>
                                                  <div className="ms-2">
                                                    <div style={menuStyle} />
                                                    <div style={menuStyle} />
                                                    <div style={menuStyle} />
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                        </Draggable>
                                      )) : <div className="text-center mt-3">
                                        {isLoading ? (
                                          <Spinner size="sm"></Spinner>
                                        ) :
                                          <>{t('No items found')}</>
                                        }
                                      </div>}
                                      {provided.placeholder}
                                    </div>
                                  )}
                                </Droppable>
                              </div>
                            </div>
                          </DragDropContext>
                        </Col>
                        <Col sm={12} md={12} xl={12} className='mt-4 pt-1 text-end'>
                          <button
                            style={{ width: '150px' }}
                            type="submit"
                            disabled={(isLoading)}
                            onClick={() => handleSubmit()}
                            className="btn btn-primary fs-14 rounded-pill">
                            {<>{t('Button Update')}</>}
                          </button>
                        </Col>
                      </Row>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            )}
          </Row>
        </Container>

        <Modal
          isOpen={!!isOpen}
          centered={true}
          // size="lg"
          toggle={() => setIsOpen(false)}
          keyboard={true}
        >
          <ModalHeader>
            {t('Configuration')}
          </ModalHeader>
          <ModalBody className="">
            <div className="row g-4">
              <Col sm={12}>
                <div className="row g-3">
                  <div className="mt-3 text-center">
                    <h6 className="mb-2 fs-19 text-center mt-1">
                      {t("Are you sure you want to update this?")}
                    </h6>
                    <p className="fs-15 mb-4 d-flex justify-content-center align-items-start align-items-sm-center text-center ">
                      {t('After confirming the update action, the data will be updated. Do you want to proceed with the update action.')}
                    </p>
                    <div className="d-flex gap-3 mt-2 justify-content-center">
                      <button
                        className="btn btn-primary rounded-pill fs-14"
                        type="button"
                        color="light"
                        onClick={() => handleSubmitConfiguration()}
                        style={{ width: '120px' }}
                      >
                        {(isLoading) ? <Spinner size="sm me-2" ></Spinner> : <>{t('Button Update')}</>}
                      </button>
                      <button
                        className="btn btn-secondary rounded-pill fs-14 ms-2"
                        type="button"
                        color="light"
                        onClick={() => setIsOpen(false)}
                        style={{ width: '120px' }}
                      >
                        {t("Button Close")}
                      </button>
                    </div>
                  </div>
                </div>
              </Col>
            </div>
          </ModalBody>
        </Modal >

        <ToastContainer closeButton={false} newestOnTop={false} pauseOnFocusLoss autoClose={2000} limit={2} />

      </div >
    </React.Fragment >
  );
};

export default Configuration;